import { useEffect, useState } from "react";
import { useARC } from "providers/ARC/ARCContext";
import { ConnectionState } from "lib/consts/ARC/connection";
import { attachRosTopicWithRetry } from "lib/ros/rosTopicSubscribeWithRetry";

export type HardwareGpsFix = {
    latitude: number | undefined;
    longitude: number | undefined;
    altitude?: number;
    fix?: number;
    satellites?: number;
    hasFix: boolean;
};

/** Live fix from `/hardware/gps` (`std_msgs/String` JSON). */
export default function useHardwareGps(): HardwareGpsFix | null {
    const { controlSocket, connectionState } = useARC();
    const [gpsFix, setGpsFix] = useState<HardwareGpsFix | null>(null);

    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected) return;

        const handleGpsUpdate = (message: unknown) => {
            try {
                const raw =
                    typeof message === "string"
                        ? message
                        : (message as { data?: string })?.data;
                const payload = typeof raw === "string" ? JSON.parse(raw) : raw;
                const gps =
                    payload?.gps && typeof payload.gps === "object"
                        ? payload.gps
                        : payload;
                const latitude = Number(gps?.latitude_deg);
                const longitude = Number(gps?.longitude_deg);
                const fix = Number(gps?.fix);
                const hasFix = Number.isFinite(fix) && fix > 0;
                const altitude = Number(gps?.altitude_m);
                const satellites = Number(gps?.satellites);

                setGpsFix((prev) => ({
                    latitude: Number.isFinite(latitude) ? latitude : prev?.latitude,
                    longitude: Number.isFinite(longitude) ? longitude : prev?.longitude,
                    altitude: Number.isFinite(altitude) ? altitude : prev?.altitude,
                    fix: Number.isFinite(fix) ? fix : prev?.fix,
                    satellites: Number.isFinite(satellites)
                        ? satellites
                        : prev?.satellites,
                    hasFix,
                }));
            } catch (error) {
                console.warn("Failed to parse /hardware/gps payload", error);
            }
        };

        return attachRosTopicWithRetry(controlSocket, "/hardware/gps", handleGpsUpdate, {
            retryUntilFirstMessage: false,
        });
    }, [controlSocket, connectionState]);

    return gpsFix;
}
